import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import {
    Loader2, Users, Trophy, Activity, Calendar, ListChecks, RefreshCw,
    Pencil, Trash2, Target, X, AlertTriangle, Send, GitBranch, ExternalLink, Save,
} from "lucide-react";

// ─── Confirmation modal ────────────────────────────────────────────────────
function ConfirmModal({ open, title, message, danger = true, onCancel, onConfirm, busy }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="confirm-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-md w-full p-6">
                <div className={`flex items-center gap-2 mb-3 ${danger ? "text-[var(--danger)]" : "text-amber-400"}`}>
                    <AlertTriangle className="h-5 w-5" />
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight">{title}</h3>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-4">{message}</p>
                <div className="flex items-center justify-end gap-2">
                    <button onClick={onCancel} data-testid="confirm-cancel" disabled={busy}
                        className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                        Cancel
                    </button>
                    <button onClick={onConfirm} disabled={busy} data-testid="confirm-ok"
                        className={`px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            danger ? "bg-[var(--danger)] hover:opacity-80" : "bg-amber-500 hover:bg-amber-400"
                        }`}>
                        {busy ? "…" : "Confirm"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Edit User modal ───────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSaved }) {
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [role, setRole] = useState(user?.role || "user");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    if (!user) return null;
    const save = async () => {
        setBusy(true);
        try {
            const body = { name, email, role };
            if (password) body.password = password;
            await api.patch(`/admin/global/users/${user.id}`, body);
            toast.success("User updated");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="edit-user-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight">Edit user</h3>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-3">
                    <Field label="Name" value={name} onChange={setName} testid="edit-user-name" />
                    <Field label="Email" value={email} onChange={setEmail} testid="edit-user-email" />
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1">Role</label>
                        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="edit-user-role"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]">
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                        </select>
                    </div>
                    <Field label="New password (leave blank to keep)" value={password} onChange={setPassword} type="password" testid="edit-user-password" />
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="edit-user-save"
                        className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--accent-hex)] text-[var(--accent-text)] hover:bg-[var(--accent-hex-hover)] disabled:opacity-40">
                        {busy ? "…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Edit Pool modal ───────────────────────────────────────────────────────
function EditPoolModal({ pool, allUsers, onClose, onSaved }) {
    const [name, setName] = useState(pool?.name || "");
    const [betAmount, setBetAmount] = useState(pool?.bet_amount ?? "");
    const [betCurrency, setBetCurrency] = useState(pool?.bet_currency || "");
    const [adminId, setAdminId] = useState(pool?.admin_id || "");
    const [betInstructions, setBetInstructions] = useState(pool?.bet_instructions || "");
    const [busy, setBusy] = useState(false);
    if (!pool) return null;
    const save = async () => {
        setBusy(true);
        try {
            const body = {
                name, bet_currency: betCurrency, bet_instructions: betInstructions,
                bet_amount: betAmount === "" || betAmount === null ? null : Number(betAmount),
                admin_id: adminId,
            };
            await api.patch(`/admin/global/pools/${pool.id}`, body);
            toast.success("Pool updated");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="edit-pool-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight">Edit pool</h3>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X className="h-4 w-4" /></button>
                </div>
                <div className="space-y-3">
                    <Field label="Name" value={name} onChange={setName} testid="edit-pool-name" />
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Bet amount" value={betAmount} onChange={setBetAmount} type="number" testid="edit-pool-bet-amount" />
                        <Field label="Currency" value={betCurrency} onChange={setBetCurrency} testid="edit-pool-bet-currency" />
                    </div>
                    <Field label="Bet instructions" value={betInstructions} onChange={setBetInstructions} testid="edit-pool-bet-instructions" />
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1">Owner (admin)</label>
                        <select value={adminId} onChange={(e) => setAdminId(e.target.value)} data-testid="edit-pool-owner"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]">
                            {allUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                        </select>
                    </div>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="edit-pool-save"
                        className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--accent-hex)] text-[var(--accent-text)] hover:bg-[var(--accent-hex-hover)] disabled:opacity-40">
                        {busy ? "…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1">{label}</label>
            <input value={value ?? ""} type={type} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]" />
        </div>
    );
}

// ─── User-pools tooltip ────────────────────────────────────────────────────
function UserPoolsTooltip({ pools }) {
    if (!pools || pools.length === 0) {
        return <span className="text-[var(--text-muted)] italic text-[10px]">(no pools)</span>;
    }
    return (
        <div role="tooltip"
            className="absolute z-30 top-full mt-1 right-0 hidden group-hover:block min-w-[200px] max-w-[320px] bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg shadow-2xl p-3 text-xs"
            data-testid="user-pools-tooltip">
            <div className="font-heading text-[9px] uppercase tracking-widest font-bold text-[var(--accent-hex)] mb-1.5">Pools</div>
            <ul className="space-y-0.5">
                {pools.map((p) => (
                    <li key={p.id} className={p.is_admin ? "font-extrabold text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}>
                        {p.name}{p.is_admin ? " *" : ""}
                    </li>
                ))}
            </ul>
            <div className="mt-1.5 text-[9px] text-[var(--text-muted)]">* admin of pool</div>
        </div>
    );
}

// ─── Knockout verification panel ───────────────────────────────────────────
// Admin-only lightweight editor for the home/away teams on knockout matches.
// Includes a big "Open source of truth" link to Wikipedia's 2026 knockout
// bracket page so the admin can eyeball the diff and correct any wrong
// assignments (football-data.org's post-draw feed occasionally lags or has
// mismatches until the tournament starts).
function KnockoutVerifyPanel() {
    const [matches, setMatches] = useState([]);
    const [teams, setTeams] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    // Per-match dirty overlay keyed by match id: { home_team_id, away_team_id }.
    const [drafts, setDrafts] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const [mres, tres] = await Promise.all([api.get("/matches"), api.get("/teams")]);
            const ko = (mres.data || []).filter((m) => m.stage === "knockout");
            ko.sort((a, b) => (a.kickoff || "").localeCompare(b.kickoff || ""));
            setMatches(ko);
            const tmap = {};
            (tres.data || []).forEach((tm) => { tmap[tm.id] = tm; });
            setTeams(tmap);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const teamOptions = Object.values(teams).sort((a, b) => a.name.localeCompare(b.name));

    const updateDraft = (mid, key, value) => {
        setDrafts((prev) => ({ ...prev, [mid]: { ...(prev[mid] || {}), [key]: value } }));
    };

    const savedValue = (m, key) => (drafts[m.id]?.[key] !== undefined ? drafts[m.id][key] : m[key]);
    const isDirty = (m) => {
        const d = drafts[m.id];
        if (!d) return false;
        return (d.home_team_id !== undefined && d.home_team_id !== m.home_team_id)
            || (d.away_team_id !== undefined && d.away_team_id !== m.away_team_id);
    };

    const save = async (m) => {
        const d = drafts[m.id] || {};
        const payload = {};
        if (d.home_team_id !== undefined && d.home_team_id !== m.home_team_id) payload.home_team_id = d.home_team_id || null;
        if (d.away_team_id !== undefined && d.away_team_id !== m.away_team_id) payload.away_team_id = d.away_team_id || null;
        if (Object.keys(payload).length === 0) return;
        setSavingId(m.id);
        try {
            await api.patch(`/admin/matches/${m.id}`, payload);
            toast.success(`Saved · ${m.round}`);
            setMatches((prev) => prev.map((x) => x.id === m.id ? { ...x, ...payload } : x));
            setDrafts((prev) => { const nx = { ...prev }; delete nx[m.id]; return nx; });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSavingId(null); }
    };

    const [seeding, setSeeding] = useState(false);
    const seedOfficial = async () => {
        if (!window.confirm("This overwrites team assignments for R32 with the official 2026 pairings, and assigns official match numbers 73-104 across all knockout rounds. Continue?")) return;
        setSeeding(true);
        try {
            const { data } = await api.post("/admin/matches/seed-official-numbers");
            const warn = data.warnings?.length ? ` (${data.warnings.length} warning${data.warnings.length === 1 ? "" : "s"})` : "";
            toast.success(`Seeded ${data.matches_updated} matches${warn}`);
            data.warnings?.forEach((w) => toast.warning(w));
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSeeding(false); }
    };

    const ROUND_ORDER = ["Round of 32", "Round of 16", "Quarter-finals", "Semi-finals", "Final", "Third place"];
    const grouped = ROUND_ORDER
        .map((r) => [r, matches.filter((m) => m.round === r)])
        .filter(([, list]) => list.length > 0);

    if (loading) {
        return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>;
    }

    return (
        <div className="space-y-6" data-testid="gadmin-knockout">
            <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-5">
                <h3 className="font-heading text-xs uppercase tracking-[0.25em] font-bold text-[var(--text-muted)] mb-3">Source of truth</h3>
                <p className="text-sm text-[var(--text-secondary)] mb-3">
                    Compare our knockout assignments against Wikipedia&apos;s live-updated bracket
                    (edited by many contributors within minutes of each real-world result). Adjust
                    any wrong home/away below and press Save.
                </p>
                <a
                    href="https://en.wikipedia.org/wiki/2026_FIFA_World_Cup_knockout_stage"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="wikipedia-source-link"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-[var(--accent-border)] text-[var(--accent-hex)] hover:bg-[var(--accent-hex)] hover:text-[var(--accent-text)] transition-colors text-xs font-extrabold uppercase tracking-widest"
                >
                    <ExternalLink className="h-3.5 w-3.5" /> Open Wikipedia bracket
                </a>
                <button
                    onClick={seedOfficial}
                    disabled={seeding}
                    data-testid="seed-official-numbers"
                    className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded border border-amber-500/50 text-amber-400 hover:bg-amber-500 hover:text-black transition-colors text-xs font-extrabold uppercase tracking-widest disabled:opacity-40"
                >
                    {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitBranch className="h-3.5 w-3.5" />}
                    Seed official 73-104 + R32 pairings
                </button>
            </div>

            {grouped.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-8">No knockout fixtures loaded yet.</p>
            ) : (
                grouped.map(([round, list]) => (
                    <div key={round}>
                        <h4 className="font-heading text-sm uppercase tracking-[0.25em] font-bold text-[var(--accent-hex)] mb-2">{round}</h4>
                        <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] overflow-hidden">
                            {list.map((m, i) => (
                                <div key={m.id} data-testid={`ko-row-${m.id}`}
                                    className={`grid grid-cols-[4rem_9rem_1fr_2rem_1fr_5rem] gap-3 items-center px-4 py-3 ${i > 0 ? "border-t border-[var(--border-muted)]" : ""}`}>
                                    <div className="font-mono text-xs font-extrabold text-amber-400 tabular-nums" title="Official FIFA match number">
                                        {m.official_number != null ? `#${m.official_number}` : "—"}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-widest font-mono text-[var(--text-muted)]">
                                        {m.kickoff ? new Date(m.kickoff).toLocaleString() : "—"}
                                    </div>
                                    <select
                                        value={savedValue(m, "home_team_id") || ""}
                                        onChange={(e) => updateDraft(m.id, "home_team_id", e.target.value || null)}
                                        data-testid={`ko-home-${m.id}`}
                                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]"
                                    >
                                        <option value="">— TBD —</option>
                                        {teamOptions.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                                    </select>
                                    <span className="text-center text-[var(--text-muted)] text-xs">vs</span>
                                    <select
                                        value={savedValue(m, "away_team_id") || ""}
                                        onChange={(e) => updateDraft(m.id, "away_team_id", e.target.value || null)}
                                        data-testid={`ko-away-${m.id}`}
                                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]"
                                    >
                                        <option value="">— TBD —</option>
                                        {teamOptions.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                                    </select>
                                    <button
                                        onClick={() => save(m)}
                                        disabled={!isDirty(m) || savingId === m.id}
                                        data-testid={`ko-save-${m.id}`}
                                        className="inline-flex items-center justify-center gap-1 bg-[var(--accent-hex)] text-[var(--accent-text)] text-[10px] font-extrabold uppercase tracking-widest px-3 py-2 rounded hover:bg-[var(--accent-hex-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        {savingId === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                        Save
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}


// ─── Broadcast panel ───────────────────────────────────────────────────────
// ─── Admin Bonus Editor ────────────────────────────────────────────────────
// Super-admin override for editing any user's bonus predictions in any pool,
// bypassing the "first kickoff" lock. Recomputes pool scores on save.
function AdminBonusEditorPanel({ pools }) {
    const [poolId, setPoolId] = useState("");
    const [members, setMembers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingUid, setSavingUid] = useState(null);
    const [drafts, setDrafts] = useState({}); // { uid: { top_scorer, gold_team_id, ... } }

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/teams");
                setTeams((data || []).sort((a, b) => a.name.localeCompare(b.name)));
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        })();
    }, []);

    useEffect(() => {
        if (!poolId) { setMembers([]); return; }
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/admin/pools/${poolId}/bonus`);
                setMembers(data.members || []);
                setDrafts({});
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
            finally { setLoading(false); }
        })();
    }, [poolId]);

    const upd = (uid, key, v) => setDrafts((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), [key]: v } }));
    const val = (m, key) => (drafts[m.user_id]?.[key] !== undefined ? drafts[m.user_id][key] : (m.prediction?.[key] || ""));
    const isDirty = (m) => !!drafts[m.user_id] && Object.keys(drafts[m.user_id]).length > 0;

    const save = async (m) => {
        const cur = m.prediction || {};
        const d = drafts[m.user_id] || {};
        const payload = {
            top_scorer: d.top_scorer !== undefined ? d.top_scorer : (cur.top_scorer || ""),
            gold_team_id: d.gold_team_id !== undefined ? d.gold_team_id : (cur.gold_team_id || ""),
            silver_team_id: d.silver_team_id !== undefined ? d.silver_team_id : (cur.silver_team_id || ""),
            bronze_team_id: d.bronze_team_id !== undefined ? d.bronze_team_id : (cur.bronze_team_id || ""),
            most_goals_team_id: d.most_goals_team_id !== undefined ? d.most_goals_team_id : (cur.most_goals_team_id || ""),
            most_conceded_team_id: d.most_conceded_team_id !== undefined ? d.most_conceded_team_id : (cur.most_conceded_team_id || ""),
        };
        setSavingUid(m.user_id);
        try {
            await api.put(`/admin/pools/${poolId}/bonus/${m.user_id}`, payload);
            toast.success(`Saved bonus for ${m.user_name}`);
            setMembers((prev) => prev.map((x) => x.user_id === m.user_id ? { ...x, prediction: payload } : x));
            setDrafts((prev) => { const nx = { ...prev }; delete nx[m.user_id]; return nx; });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setSavingUid(null); }
    };

    const TEAM_KEYS = [
        { key: "gold_team_id", label: "🥇 Gold" },
        { key: "silver_team_id", label: "🥈 Silver" },
        { key: "bronze_team_id", label: "🥉 Bronze" },
        { key: "most_goals_team_id", label: "Most goals" },
        { key: "most_conceded_team_id", label: "Most conceded" },
    ];

    return (
        <div className="space-y-4" data-testid="gadmin-bonus-editor">
            <select value={poolId} onChange={(e) => setPoolId(e.target.value)} data-testid="bonus-editor-pool-select"
                className="bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm">
                <option value="">— Select pool —</option>
                {pools.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {loading && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>}
            {!loading && members.map((m) => (
                <div key={m.user_id} className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-4" data-testid={`bonus-edit-${m.user_id}`}>
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="font-heading text-base font-extrabold text-[var(--text-primary)]">{m.user_name}</div>
                            <div className="text-[10px] text-[var(--text-muted)]">{m.user_email}</div>
                        </div>
                        <button onClick={() => save(m)} disabled={!isDirty(m) || savingUid === m.user_id}
                            data-testid={`bonus-save-${m.user_id}`}
                            className="inline-flex items-center gap-1 bg-[var(--accent-hex)] text-[var(--accent-text)] text-[10px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed">
                            {savingUid === m.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save
                        </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                            Top scorer
                            <input type="text" value={val(m, "top_scorer")} onChange={(e) => upd(m.user_id, "top_scorer", e.target.value)}
                                data-testid={`bonus-scorer-${m.user_id}`}
                                className="mt-1 w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-2 py-1.5 rounded text-xs normal-case tracking-normal font-normal" />
                        </label>
                        {TEAM_KEYS.map(({ key, label }) => (
                            <label key={key} className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                                {label}
                                <select value={val(m, key)} onChange={(e) => upd(m.user_id, key, e.target.value)}
                                    data-testid={`bonus-${key}-${m.user_id}`}
                                    className="mt-1 w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-2 py-1.5 rounded text-xs normal-case tracking-normal font-normal">
                                    <option value="">—</option>
                                    {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                                </select>
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}


function BroadcastPanel({ users, pools }) {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [allUsers, setAllUsers] = useState(true);
    const [poolId, setPoolId] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState("");
    const [emailOn, setEmailOn] = useState(true);
    const [pushOn, setPushOn] = useState(true);
    const [busy, setBusy] = useState(false);

    const filtered = users.filter((u) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
    const toggleId = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    const selectAllFiltered = () => setSelectedIds(filtered.map((u) => u.id));
    const clearSelection = () => setSelectedIds([]);

    // Effective recipient count = users who satisfy BOTH the user-set and
    // the pool filter. We compute it client-side as a preview before the
    // server's authoritative count comes back in the response.
    const previewCount = (() => {
        let pool = users;
        if (poolId) {
            const member = new Set((pools.find((p) => p.id === poolId)?.members || []).map((m) => m.email));
            pool = pool.filter((u) => member.has(u.email));
        }
        if (allUsers) return pool.length;
        const sel = new Set(selectedIds);
        return pool.filter((u) => sel.has(u.id)).length;
    })();

    const channelsLabel = () => {
        const c = [];
        if (emailOn) c.push("email");
        if (pushOn) c.push("push");
        return c.length === 0 ? "(no channels)" : c.join(" + ");
    };

    const send = async () => {
        if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required"); return; }
        if (!emailOn && !pushOn) { toast.error("Pick at least one channel"); return; }
        if (!allUsers && selectedIds.length === 0) { toast.error("Pick at least one user or enable 'all users'"); return; }
        setBusy(true);
        try {
            const channels = [];
            if (emailOn) channels.push("email");
            if (pushOn) channels.push("push");
            const body_payload = {
                subject: subject.trim(), body, all_users: allUsers,
                user_ids: allUsers ? [] : selectedIds,
                pool_id: poolId || null,
                channels,
            };
            const { data } = await api.post(`/admin/global/broadcast`, body_payload);
            toast.success(`Sent to ${data.recipients} · ${data.email_sent} email · ${data.push_reached} push`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4" data-testid="gadmin-broadcast">
            <div className="space-y-3 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-5">
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1">Subject</label>
                    <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                        placeholder="A short, clear subject line"
                        data-testid="broadcast-subject"
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]" />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1">Body (plain text — line breaks preserved)</label>
                    <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} maxLength={10000}
                        data-testid="broadcast-body"
                        placeholder="Hi friends,&#10;Heads-up: we just locked in the official top scorer answer. Check your standings…"
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)] whitespace-pre-wrap font-mono" />
                </div>
                <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-[var(--border-muted)]">
                    <label className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                        <input type="checkbox" checked={emailOn} onChange={(e) => setEmailOn(e.target.checked)} data-testid="broadcast-channel-email" />
                        Email (via Resend)
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                        <input type="checkbox" checked={pushOn} onChange={(e) => setPushOn(e.target.checked)} data-testid="broadcast-channel-push" />
                        Push notification
                    </label>
                </div>
            </div>

            {/* Right column — recipients */}
            <div className="space-y-3 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-4">
                <div>
                    <label className="inline-flex items-center gap-2 text-sm font-bold text-[var(--text-primary)] cursor-pointer">
                        <input type="checkbox" checked={allUsers} onChange={(e) => setAllUsers(e.target.checked)} data-testid="broadcast-all-users" />
                        All users {!poolId && <span className="text-[var(--text-muted)] font-normal">({users.length})</span>}
                    </label>
                </div>
                <div>
                    <label className="block text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1">Filter by pool</label>
                    <select value={poolId} onChange={(e) => setPoolId(e.target.value)} data-testid="broadcast-pool-filter"
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-2 py-1.5 rounded text-xs focus:outline-none focus:border-[var(--accent-hex)]">
                        <option value="">— No filter —</option>
                        {pools.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.member_count})</option>)}
                    </select>
                </div>
                {!allUsers && (
                    <>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users…"
                            data-testid="broadcast-user-search"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-2 py-1.5 rounded text-xs focus:outline-none focus:border-[var(--accent-hex)]" />
                        <div className="flex gap-1">
                            <button type="button" onClick={selectAllFiltered}
                                className="px-2 py-1 rounded text-[9px] uppercase tracking-widest font-bold border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                                Select filtered
                            </button>
                            <button type="button" onClick={clearSelection}
                                className="px-2 py-1 rounded text-[9px] uppercase tracking-widest font-bold border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                                Clear
                            </button>
                        </div>
                        <div className="max-h-[280px] overflow-y-auto border border-[var(--border-muted)] rounded p-1.5 space-y-0.5 bg-[var(--bg-base)]" data-testid="broadcast-user-list">
                            {filtered.map((u) => (
                                <label key={u.id} className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-[var(--overlay-light)] cursor-pointer">
                                    <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => toggleId(u.id)} data-testid={`broadcast-pick-${u.id}`} />
                                    <span className="font-bold text-[var(--text-primary)] truncate">{u.name}</span>
                                    <span className="text-[var(--text-muted)] truncate ml-auto">{u.email}</span>
                                </label>
                            ))}
                            {filtered.length === 0 && (
                                <div className="text-center py-4 text-[var(--text-muted)] text-xs italic">No users match.</div>
                            )}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                            Selected: <span className="text-[var(--accent-hex)]">{selectedIds.length}</span>
                        </div>
                    </>
                )}

                <div className="pt-3 border-t border-[var(--border-muted)] space-y-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                        Recipients: <span className="text-[var(--text-primary)]">{previewCount}</span><br />
                        Channels: <span className="text-[var(--text-primary)]">{channelsLabel()}</span>
                    </div>
                    <button onClick={send} disabled={busy || !subject.trim() || !body.trim() || previewCount === 0 || (!emailOn && !pushOn)}
                        data-testid="broadcast-send"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-widest text-xs px-3 py-2.5 rounded hover:bg-[var(--accent-hex-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
                        <Send className="h-3.5 w-3.5" /> {busy ? "Sending…" : `Send to ${previewCount}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function GlobalAdmin() {
    const { t, lang } = useI18n();
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [pools, setPools] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editUser, setEditUser] = useState(null);
    const [editPool, setEditPool] = useState(null);
    const [confirm, setConfirm] = useState(null); // {title, message, action}
    const [topScorer, setTopScorer] = useState("");
    const [topScorerBusy, setTopScorerBusy] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [ov, us, po, lg] = await Promise.all([
                api.get("/admin/global/overview"),
                api.get("/admin/global/users"),
                api.get("/admin/global/pools"),
                api.get("/admin/sync-log"),
            ]);
            setOverview(ov.data); setUsers(us.data); setPools(po.data); setLogs(lg.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setLoading(false); }
    };
    useEffect(() => { loadAll(); }, []);

    const fmtTime = (iso) => {
        if (!iso) return "—";
        try {
            const locale = lang === "sv" ? "sv-SE" : undefined;
            return new Date(iso).toLocaleString(locale);
        } catch { return iso; }
    };

    const doDeleteUser = (u) => setConfirm({
        title: `Delete user`,
        message: `This permanently deletes ${u.email} and all their predictions/memberships. Pools they own will be re-assigned to you. This cannot be undone.`,
        action: async () => {
            await api.delete(`/admin/global/users/${u.id}`);
            toast.success("User deleted");
            loadAll();
        },
    });

    const doDeletePool = (p) => setConfirm({
        title: `Delete pool`,
        message: `This permanently deletes "${p.name}" and all its predictions/members. This cannot be undone.`,
        action: async () => {
            await api.delete(`/admin/global/pools/${p.id}`);
            toast.success("Pool deleted");
            loadAll();
        },
    });

    const runConfirm = async () => {
        if (!confirm) return;
        try { await confirm.action(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setConfirm(null); }
    };

    const setTopScorerEverywhere = async () => {
        const name = topScorer.trim();
        if (!name) { toast.error("Top scorer is empty"); return; }
        setTopScorerBusy(true);
        try {
            const { data } = await api.put(`/admin/global/top-scorer`, { top_scorer: name });
            toast.success(`Top scorer set on ${data.pools_updated} pool${data.pools_updated === 1 ? "" : "s"}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setTopScorerBusy(false); }
    };

    if (loading) {
        return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>;
    }

    const inactiveIds = new Set(overview?.inactive_pool_ids || []);

    const TABS = [
        { id: "overview", label: t("gadmin.tab.overview"), icon: Activity },
        { id: "users", label: t("gadmin.tab.users"), icon: Users },
        { id: "pools", label: t("gadmin.tab.pools"), icon: Trophy },
        { id: "bonus", label: "Top scorer", icon: Target },
        { id: "bonus-edit", label: "Bonus edit", icon: Target },
        { id: "broadcast", label: "Broadcast", icon: Send },
        { id: "knockout", label: "Knockout verify", icon: GitBranch },
        { id: "log", label: t("gadmin.tab.log"), icon: ListChecks },
    ];

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="global-admin-page">
            <div className="flex items-end justify-between mb-6 gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--accent-hex)]">{t("gadmin.eyebrow")}</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-[var(--text-primary)] mt-1">
                        {t("gadmin.title")}
                    </h1>
                </div>
                <button onClick={loadAll} data-testid="gadmin-refresh"
                    className="inline-flex items-center gap-2 border border-[var(--border-base)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-md">
                    <RefreshCw className="h-3.5 w-3.5" /> {t("leader.refresh")}
                </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-[var(--border-base)] mb-6 overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                    {TABS.map((tt) => {
                        const Icon = tt.icon;
                        const active = tab === tt.id;
                        return (
                            <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`gadmin-tab-${tt.id}`}
                                className={`relative flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
                                    active ? "text-[var(--accent-hex)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                }`}>
                                <Icon className="h-4 w-4" /> {tt.label}
                                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-hex)]" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {tab === "overview" && overview && (
                <div className="space-y-4" data-testid="gadmin-overview">
                    {overview.inactive_pools_7d > 0 && (
                        <button
                            type="button"
                            onClick={() => setTab("pools")}
                            data-testid="gadmin-inactive-badge"
                            className="w-full text-left p-4 rounded-lg border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/15 transition-colors flex items-center gap-3"
                        >
                            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />
                            <div className="flex-1">
                                <div className="font-heading text-lg font-extrabold uppercase tracking-tight text-amber-300">
                                    {overview.inactive_pools_7d} {overview.inactive_pools_7d === 1 ? "pool" : "pools"} inactive 7d+
                                </div>
                                <div className="text-xs text-amber-200/80 mt-0.5">
                                    No admin activity (pool edit, admin prediction, or bonus update) in the last 7 days. Click to inspect.
                                </div>
                            </div>
                            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-400">View →</span>
                        </button>
                    )}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {[
                            { label: t("gadmin.stat.users"), value: overview.users, icon: Users },
                            { label: t("gadmin.stat.pools"), value: overview.pools, icon: Trophy },
                            { label: t("gadmin.stat.matches"), value: overview.matches, icon: Calendar },
                            { label: t("gadmin.stat.finished"), value: overview.finished_matches, icon: Activity },
                            { label: t("gadmin.stat.predictions"), value: overview.predictions, icon: ListChecks },
                        ].map((s) => {
                            const Icon = s.icon;
                            return (
                                <div key={s.label} className="p-4 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]">
                                    <Icon className="h-4 w-4 text-[var(--accent-hex)] mb-2" />
                                    <div className="font-heading text-4xl font-extrabold tabular-nums text-[var(--text-primary)]">{s.value}</div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mt-1">{s.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {tab === "users" && (
                <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] overflow-visible" data-testid="gadmin-users">
                    <div className="grid grid-cols-[1fr_2fr_5rem_4rem_4rem_8rem_5rem] gap-2 px-4 py-2 border-b border-[var(--border-muted)] text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                        <div>{t("gadmin.users.name")}</div>
                        <div>{t("gadmin.users.email")}</div>
                        <div>{t("gadmin.users.role")}</div>
                        <div className="text-right">{t("gadmin.users.pools")}</div>
                        <div className="text-right">{t("gadmin.users.picks")}</div>
                        <div className="text-right">{t("gadmin.users.created")}</div>
                        <div className="text-right">Actions</div>
                    </div>
                    {users.map((u) => (
                        <div key={u.id} className="relative group grid grid-cols-[1fr_2fr_5rem_4rem_4rem_8rem_5rem] gap-2 px-4 py-2 border-b border-[var(--border-muted)] last:border-b-0 text-sm hover:bg-[var(--overlay-light)]" data-testid={`gadmin-user-${u.id}`}>
                            <div className="font-heading font-bold uppercase tracking-tight text-[var(--text-primary)] truncate">{u.name}</div>
                            <div className="text-[var(--text-secondary)] truncate">{u.email}</div>
                            <div>
                                <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                                    u.role === "admin" ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "bg-[var(--overlay-light)] text-[var(--text-muted)]"
                                }`}>{u.role}</span>
                            </div>
                            <div className="text-right tabular-nums text-[var(--text-primary)] cursor-help" data-testid={`gadmin-user-pools-${u.id}`}>
                                {u.pool_count}
                                <UserPoolsTooltip pools={u.pools} />
                            </div>
                            <div className="text-right tabular-nums text-[var(--text-primary)]">{u.prediction_count}</div>
                            <div className="text-right text-[var(--text-muted)] text-xs">{u.created_at?.slice(0, 10)}</div>
                            <div className="flex items-center justify-end gap-1">
                                <button onClick={() => setEditUser(u)} data-testid={`gadmin-user-edit-${u.id}`}
                                    className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--accent-hex)] hover:bg-[var(--overlay-light)]" title="Edit">
                                    <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => doDeleteUser(u)} data-testid={`gadmin-user-delete-${u.id}`}
                                    className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--danger)] hover:bg-[var(--overlay-light)]" title="Delete">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "pools" && (
                <div className="space-y-2" data-testid="gadmin-pools">
                    {pools.length === 0 && (
                        <div className="text-center py-12 text-[var(--text-muted)]">{t("gadmin.pools.empty")}</div>
                    )}
                    {pools.map((p) => {
                        const isInactive = inactiveIds.has(p.id);
                        return (
                        <div key={p.id} className={`p-4 rounded-lg border bg-[var(--bg-card)] ${isInactive ? "border-amber-500/50" : "border-[var(--border-base)]"}`} data-testid={`gadmin-pool-${p.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Link to={`/pools/${p.id}`} className="font-heading text-lg font-bold uppercase tracking-tight text-[var(--text-primary)] hover:text-[var(--accent-hex)]">
                                            {p.name}
                                        </Link>
                                        {isInactive && (
                                            <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid={`gadmin-pool-inactive-${p.id}`}>
                                                <AlertTriangle className="h-3 w-3" /> 7d+ idle
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-[var(--text-muted)] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                        <span>{t("gadmin.pools.admin")}: <span className="text-[var(--text-secondary)]">{p.admin.name} ({p.admin.email})</span></span>
                                        <span>Code: <span className="font-mono text-[var(--accent-hex)]">{p.code}</span></span>
                                        {(p.bet_amount != null && p.bet_amount > 0) && (
                                            <span>Bet: <span className="text-amber-400 font-bold">{p.bet_amount}{p.bet_currency ? ` ${p.bet_currency}` : ""}</span></span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-[var(--accent-tint)] text-[var(--accent-hex)]">
                                        {p.member_count} {t("gadmin.pools.members")}
                                    </span>
                                    <button onClick={() => setEditPool(p)} data-testid={`gadmin-pool-edit-${p.id}`}
                                        className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--accent-hex)] hover:bg-[var(--overlay-light)]" title="Edit">
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button onClick={() => doDeletePool(p)} data-testid={`gadmin-pool-delete-${p.id}`}
                                        className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--danger)] hover:bg-[var(--overlay-light)]" title="Delete">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {p.members.map((m, i) => (
                                    <span key={`${p.id}-m-${m.email || m.name || i}`} className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded bg-[var(--overlay-light)] text-[var(--text-secondary)]">
                                        {m.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {tab === "bonus" && (
                <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 max-w-2xl" data-testid="gadmin-topscorer">
                    <div className="flex items-center gap-2 mb-3">
                        <Target className="h-5 w-5 text-[var(--accent-hex)]" />
                        <h3 className="font-heading text-xl font-extrabold uppercase tracking-tight text-[var(--text-primary)]">Top scorer (all pools)</h3>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mb-4">
                        Set the official top-scorer answer on <strong>every pool at once</strong>. This triggers a leaderboard recompute so bonus points propagate immediately. To reset for a specific pool, edit the pool&apos;s bonus answers individually.
                        <br /><span className="text-xs">For a tie in the golden boot, list every winner separated by commas (e.g. <em>Messi, Haaland</em>). A user&apos;s prediction scores if it fuzzy-matches ANY of the listed names.</span>
                    </p>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1.5">Top scorer (full player name, or comma-separated for ties)</label>
                    <div className="flex gap-2">
                        <input value={topScorer} onChange={(e) => setTopScorer(e.target.value)} placeholder="e.g. Lionel Messi  —  or: Messi, Haaland"
                            data-testid="gadmin-topscorer-input"
                            className="flex-1 bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded text-sm focus:outline-none focus:border-[var(--accent-hex)]" />
                        <button onClick={setTopScorerEverywhere} disabled={!topScorer.trim() || topScorerBusy}
                            data-testid="gadmin-topscorer-set"
                            className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--accent-hex)] text-[var(--accent-text)] hover:bg-[var(--accent-hex-hover)] disabled:opacity-40 disabled:cursor-not-allowed">
                            {topScorerBusy ? "…" : "Set on all pools"}
                        </button>
                    </div>
                </div>
            )}

            {tab === "broadcast" && (
                <BroadcastPanel users={users} pools={pools} />
            )}

            {tab === "knockout" && (
                <KnockoutVerifyPanel />
            )}

            {tab === "bonus-edit" && (
                <AdminBonusEditorPanel pools={pools} />
            )}

            {tab === "log" && (
                <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] overflow-hidden" data-testid="gadmin-log">
                    {logs.length === 0 && (
                        <div className="text-center py-12 text-[var(--text-muted)]">{t("gadmin.log.empty")}</div>
                    )}
                    {logs.map((l) => (
                        <div key={l.id} className="grid grid-cols-[10rem_1fr_5rem_8rem] gap-3 px-4 py-2 border-b border-[var(--border-muted)] last:border-b-0 text-sm">
                            <div className="text-[var(--text-muted)] text-xs font-mono">{fmtTime(l.at)}</div>
                            <div className="text-[var(--text-secondary)] truncate">
                                <span className="font-bold uppercase text-[var(--text-primary)]">{l.type}</span>
                                {l.source && <span className="text-[var(--text-muted)] text-xs"> · {l.source}</span>}
                                {l.message && <span className="text-[var(--danger)] text-xs"> · {l.message}</span>}
                            </div>
                            <div>
                                <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                                    l.status === "ok" ? "bg-[var(--success-tint)] text-[var(--success)]" :
                                    l.status === "skipped" ? "bg-[var(--overlay-light)] text-[var(--text-muted)]" :
                                    "bg-[var(--danger-tint)] text-[var(--danger)]"
                                }`}>{l.status}</span>
                            </div>
                            <div className="text-right text-xs text-[var(--text-muted)] font-mono">
                                {l.updated != null && <span>+{l.updated} </span>}
                                {l.results_changed != null && <span>({l.results_changed} ch)</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); loadAll(); }} />
            <EditPoolModal pool={editPool} allUsers={users} onClose={() => setEditPool(null)} onSaved={() => { setEditPool(null); loadAll(); }} />
            <ConfirmModal open={!!confirm} title={confirm?.title} message={confirm?.message}
                onCancel={() => setConfirm(null)} onConfirm={runConfirm} />
        </div>
    );
}
